import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Checkbox,
  Chip,
  EmptyState,
  ErrorState,
  Field,
  FormActions,
  FormSection,
  Input,
  Nota,
  PageHeader,
  Skeleton,
  Tabs,
  Textarea,
  idDaAba,
  idDoPainel,
} from '../../../ui';

import HistoricoEdicoes from '../../../components/ti/HistoricoEdicoes';
import ResumoRegistro from '../../../components/ti/ResumoRegistro';

import { TITERMOS } from '../routes';
import {
  PlaceholderTermo,
  TermoTemplate,
  TermoTemplateHistorico,
  UpsertTermoTemplate,
  buscarTemplate,
  historicoTemplate,
  listarPlaceholders,
  previewTemplate,
  salvarTemplate,
} from '../../../api/termo-template/termoTemplateApi';
import { renderizarPrevia } from '../../../utils/termoMarkdown';
import { formatarDataCurta } from '../../../utils/data';
import {
  listarProdutosDisponivel,
  motivoListarProdutosTravado,
  rotuloSituacaoTemplate,
} from '../../../utils/termoTemplateRegras';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * A ficha do template de um tipo de termo (at-69, frente D/D7).
 *
 * 🔴 **É a peça mais delicada da branch, e o defeito que ela corrige é de DADO
 * JURÍDICO.** O modal antigo (`ModalEditarTemplate`, 692 linhas) buscava o
 * template num `try` cujo `catch (err: any)` **não usava `err`**: 404, 500 e falha
 * de rede caíam no mesmo ramo, que **zerava os 9 estados com defaults**
 * (`corpo = ''`, flags `true`) e mostrava o formulário **como se fosse um template
 * novo**. Se o TI, achando que o texto sumiu, digitasse algo e salvasse, o
 * `PUT /termo-template/:codigo` gravaria o que estava na tela **por cima das
 * cláusulas reais** — e ainda publicaria uma versão (at-40). Sem aviso nenhum.
 *
 * **Aqui o formulário só é montado com o dado que veio do servidor.** Qualquer
 * falha vira `ErrorState` com "Tentar novamente"; 404 (código fora do catálogo)
 * vira `EmptyState` com o caminho de volta.
 *
 * ## As outras correções
 *
 *   - **Os placeholders vêm só do servidor.** Havia um fallback local de 4, usado
 *     em silêncio quando `listarPlaceholders` falhava — ele podia anunciar um campo
 *     que o PDF não preenche. Agora a falha vira `Nota atencao` e a lista fica
 *     vazia: melhor não oferecer do que oferecer o que não existe.
 *   - **A prévia diz que é APROXIMADA.** Ela é um segundo parser do markdown (ver
 *     `utils/termoMarkdown.tsx`), e o documento real é o da pré-visualização em PDF.
 *   - **Os `warnings` do salvamento viram `Nota atencao` persistente**, não só um
 *     `toast.warn` que passa: é aviso sobre o documento (placeholder desconhecido
 *     sai literal no contrato).
 *   - **O `useSelector(accessToken)` saiu.** O editor antigo tinha `if (!token)
 *     return` na carga: sem token ele abria **em branco e mudo**. O Bearer é
 *     injetado pelo interceptor desde a at-17.
 */

type Estado = 'carregando' | 'ok' | 'erro' | 'inexistente';
type Aba = 'conteudo' | 'historico';

const ID_BASE = 'termo-ficha';

const ABAS = [
  { id: 'conteudo', rotulo: 'Conteúdo' },
  { id: 'historico', rotulo: 'Histórico' },
];

const TermoFichaPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(TITERMOS());
  const { codigo = '' } = useParams<{ codigo: string }>();

  const corpoRef = useRef<HTMLTextAreaElement>(null);

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState<Aba>('conteudo');

  const [template, setTemplate] = useState<TermoTemplate | null>(null);

  const [nome, setNome] = useState('');
  const [titulo, setTitulo] = useState('');
  const [corpo, setCorpo] = useState('');
  const [mostrarEndereco, setMostrarEndereco] = useState(true);
  const [mostrarProdutos, setMostrarProdutos] = useState(true);
  const [mostrarValores, setMostrarValores] = useState(true);
  const [listarProdutos, setListarProdutos] = useState(true);
  const [assinaturaConsultor, setAssinaturaConsultor] = useState(true);
  const [ativo, setAtivo] = useState(true);

  const [salvando, setSalvando] = useState(false);
  const [previewando, setPreviewando] = useState(false);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [avisos, setAvisos] = useState<string[]>([]);

  const [placeholders, setPlaceholders] = useState<PlaceholderTermo[]>([]);
  const [erroPlaceholders, setErroPlaceholders] = useState('');

  const [historico, setHistorico] = useState<TermoTemplateHistorico[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState('');

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      const t = await buscarTemplate(codigo);

      // 🔴 O formulário só nasce com o que veio do servidor — ver o aviso no topo.
      setTemplate(t);
      setNome(t.nome ?? '');
      setTitulo(t.titulo ?? '');
      setCorpo(t.corpo ?? '');
      setMostrarEndereco(t.mostrarEndereco ?? true);
      setMostrarProdutos(t.mostrarProdutos ?? true);
      setMostrarValores(t.mostrarValores ?? true);
      setListarProdutos(t.listarProdutos ?? true);
      setAssinaturaConsultor(t.assinaturaConsultor ?? true);
      setAtivo(t.ativo ?? true);
      setEstado('ok');
    } catch (falha) {
      // 404 é "este código não é tipo do catálogo" — outra coisa que falha de rede.
      if ((falha as any)?.response?.status === 404) {
        setEstado('inexistente');
        return;
      }

      setErro(extrairMensagemErro(falha, 'Não foi possível carregar o template.'));
      setEstado('erro');
    }
  }, [codigo]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /* Os placeholders vêm do backend (fonte única: `placeholders.registry.ts`).
     ⚠️ SEM fallback local: a lista de 4 que existia aqui podia anunciar um campo
     que o motor não resolve, e aí o contrato sai com a lacuna literal. */
  useEffect(() => {
    let vivo = true;

    listarPlaceholders()
      .then((lista) => {
        if (vivo) setPlaceholders(lista ?? []);
      })
      .catch((falha) => {
        if (!vivo) return;
        setPlaceholders([]);
        setErroPlaceholders(
          extrairMensagemErro(falha, 'Não foi possível carregar a lista de placeholders.'),
        );
      });

    return () => {
      vivo = false;
    };
  }, []);

  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true);
    setErroHistorico('');
    try {
      setHistorico(await historicoTemplate(codigo));
    } catch (falha) {
      setHistorico([]);
      setErroHistorico(extrairMensagemErro(falha, 'Falha ao ler o histórico.'));
    } finally {
      setCarregandoHistorico(false);
    }
  }, [codigo]);

  useEffect(() => {
    if (aba === 'historico') void carregarHistorico();
  }, [aba, carregarHistorico]);

  const inserirPlaceholder = (chave: string) => {
    const marcador = `{{${chave}}}`;
    const campo = corpoRef.current;

    if (!campo) {
      setCorpo((atual) => atual + marcador);
      return;
    }

    const inicio = campo.selectionStart;
    const fim = campo.selectionEnd;
    setCorpo(corpo.substring(0, inicio) + marcador + corpo.substring(fim));
    setErros((atual) => ({ ...atual, corpo: '' }));

    /* O reposicionamento precisa do próximo quadro: o valor do textarea só existe
       depois do re-render. ⚠️ Com cleanup — o componente pode desmontar antes. */
    const timer = setTimeout(() => {
      campo.focus();
      campo.setSelectionRange(inicio + marcador.length, inicio + marcador.length);
    }, 50);
    return () => clearTimeout(timer);
  };

  const rolarAte = (campo: string) => {
    const alvo = document.querySelector<HTMLElement>(`[data-field="${campo}"]`);
    // Defensivo: `scrollIntoView` não existe no jsdom nem em WebView antiga.
    if (typeof alvo?.scrollIntoView === 'function') {
      alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  /** As três regras que o editor sempre teve — agora num lugar só. */
  const validar = (): boolean => {
    const novos: Record<string, string> = {};
    if (!nome.trim()) novos.nome = 'O nome do tipo de termo é obrigatório.';
    if (!titulo.trim()) novos.titulo = 'O título impresso no PDF é obrigatório.';
    if (!corpo.trim()) novos.corpo = 'O corpo das cláusulas é obrigatório.';

    setErros(novos);
    if (Object.keys(novos).length > 0) {
      rolarAte(Object.keys(novos)[0]);
      return false;
    }
    return true;
  };

  const montarPayload = (): UpsertTermoTemplate => ({
    nome: nome.trim(),
    titulo: titulo.trim(),
    corpo: corpo.trim(),
    mostrarEndereco,
    mostrarProdutos,
    mostrarValores,
    listarProdutos,
    assinaturaConsultor,
    ativo,
  });

  const preVisualizar = async () => {
    if (!validar()) return;

    setPreviewando(true);
    try {
      const blob = await previewTemplate(montarPayload());
      const url = URL.createObjectURL(blob);
      const janela = window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);

      // Pop-up bloqueado não é falha — é configuração do navegador. Sem o aviso,
      // nada abre e nada é dito, e o clique parece não ter funcionado.
      if (!janela) {
        toast.info('Permita pop-ups deste site para abrir a pré-visualização em PDF.');
      }
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao gerar a pré-visualização.'));
    } finally {
      setPreviewando(false);
    }
  };

  const salvar = async () => {
    if (!validar()) return;

    setSalvando(true);
    setAvisos([]);
    try {
      const resposta = await salvarTemplate(codigo, montarPayload());

      /* 🔴 Os avisos do backend (placeholder desconhecido, por exemplo) ficam na
         tela: um `toast.warn` passa, e isto é aviso sobre o DOCUMENTO — o
         placeholder que o motor não resolve sai literal no contrato. */
      if (resposta.warnings && resposta.warnings.length > 0) {
        setAvisos(resposta.warnings);
        toast.warn('Template salvo com avisos. Veja o aviso na tela.');
      } else {
        toast.success('Template salvo com sucesso.');
      }

      setTemplate((atual) => (atual ? { ...atual, ...resposta } : resposta));
      if (aba === 'historico') void carregarHistorico();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao salvar o template.'));
    } finally {
      setSalvando(false);
    }
  };

  const listarProdutosLiberado = listarProdutosDisponivel(mostrarProdutos, mostrarValores);
  const motivoListarTravado = motivoListarProdutosTravado(mostrarProdutos, mostrarValores);

  const painelConteudo = () => (
    <div className="tw-flex tw-flex-col tw-gap-8">
      {avisos.length > 0 && (
        <Nota variante="atencao" aria-live="polite">
          <strong>O template foi salvo, mas o servidor apontou o seguinte:</strong>
          <ul className="tw-mb-0 tw-mt-1.5 tw-pl-5">
            {avisos.map((aviso) => (
              <li key={aviso}>{aviso}</li>
            ))}
          </ul>
        </Nota>
      )}

      <FormSection titulo="Identificação" icone="identificacao">
        {/* Nome do TIPO × título impresso: os dois divergem com frequência (tipo
            "COMODATO", título "CONTRATO DE COMODATO - RASTREAMENTO"). */}
        <Field
          rotulo="Nome do tipo de termo"
          obrigatorio
          dica="Aparece nas telas do sistema."
          erro={erros.nome}
          campo="nome"
        >
          <Input
            value={nome}
            placeholder="Ex.: PROTEÇÃO COMPLETA"
            onChange={(evento) => {
              setNome(evento.target.value);
              setErros((atual) => ({ ...atual, nome: '' }));
            }}
          />
        </Field>

        <Field
          rotulo="Título impresso no PDF"
          obrigatorio
          dica="É o cabeçalho do documento entregue ao associado."
          erro={erros.titulo}
          campo="titulo"
        >
          <Input
            value={titulo}
            placeholder="Ex.: TERMO DE ADESÃO - PROTEÇÃO COMPLETA"
            onChange={(evento) => {
              setTitulo(evento.target.value);
              setErros((atual) => ({ ...atual, titulo: '' }));
            }}
          />
        </Field>
      </FormSection>

      <FormSection titulo="Cláusulas" icone="documento">
        {erroPlaceholders ? (
          /* ⚠️ Sem fallback local: melhor não oferecer do que oferecer um campo que
             o motor talvez não resolva (§3.3). */
          <Nota variante="atencao">
            Não foi possível carregar a lista de placeholders. Você pode digitá-los à mão no
            formato <code>{'{{nome}}'}</code> — o servidor avisa ao salvar se algum não for
            reconhecido.
          </Nota>
        ) : (
          placeholders.length > 0 && (
            <div>
              <p className="tw-m-0 tw-mb-2 tw-text-sm tw-font-semibold tw-text-texto-titulo">
                Campos disponíveis — clique para inserir no cursor
              </p>
              {/* 🔴 O significado de cada placeholder era um `title`. Aqui ele fica
                  visível: é o que diz ao TI o que vai sair impresso. */}
              <dl className="tw-m-0 tw-grid tw-gap-2 sm:tw-grid-cols-2">
                {placeholders.map(({ chave, rotulo, descricao }) => (
                  <div key={chave} className="tw-flex tw-flex-col tw-gap-0.5">
                    <dt>
                      <Botao
                        variante="fantasma"
                        tamanho="sm"
                        onClick={() => inserirPlaceholder(chave)}
                      >
                        {`+ {{${chave}}}`}
                      </Botao>
                    </dt>
                    <dd className="tw-m-0 tw-pl-3 tw-text-xs tw-text-texto-suave">
                      {descricao ? `${rotulo} — ${descricao}` : rotulo}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )
        )}

        <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
          <Field
            rotulo="Corpo das cláusulas"
            obrigatorio
            dica="Markdown leve: parágrafos separados por linha em branco e **negrito**."
            erro={erros.corpo}
            campo="corpo"
          >
            <Textarea
              ref={corpoRef}
              rows={14}
              value={corpo}
              className="tw-font-mono"
              placeholder="Escreva aqui as cláusulas do termo..."
              onChange={(evento) => {
                setCorpo(evento.target.value);
                setErros((atual) => ({ ...atual, corpo: '' }));
              }}
            />
          </Field>

          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <span className="tw-text-sm tw-font-semibold tw-text-texto-titulo">Prévia</span>
            <div className="tw-min-h-[240px] tw-flex-1 tw-overflow-y-auto tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-superficie tw-p-3 tw-text-sm tw-text-texto-corpo">
              {renderizarPrevia(corpo)}
            </div>
            {/* ⚠️ A prévia é um SEGUNDO parser do markdown (§3.3). Dizer que ela é
                aproximada é o que impede o TI de tomá-la por documento. */}
            <p className="tw-m-0 tw-text-xs tw-text-texto-desabilitado">
              Prévia aproximada — o documento real é o da &quot;Pré-visualizar em PDF&quot;.
            </p>
          </div>
        </div>
      </FormSection>

      <FormSection titulo="Seções do PDF" icone="documento">
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Checkbox
            rotulo="Exibir endereço completo do associado e do veículo"
            checked={mostrarEndereco}
            onChange={setMostrarEndereco}
          />

          <Checkbox
            rotulo="Exibir tabela de produtos vinculados"
            checked={mostrarProdutos}
            onChange={setMostrarProdutos}
          />

          <Checkbox
            rotulo="Exibir tabela de valores (VALOR TOTAL)"
            descricao="Desmarcado, o PDF traz um bloco reduzido, sem a tabela de valores."
            checked={mostrarValores}
            onChange={setMostrarValores}
          />

          <Checkbox
            rotulo="Listar os produtos linha a linha"
            descricao="Marcado, discrimina taxa administrativa, rateio e cada produto contratado. Desmarcado, mostra apenas o VALOR TOTAL."
            checked={listarProdutos}
            disabled={!listarProdutosLiberado}
            motivoDesabilitado={motivoListarTravado ?? undefined}
            onChange={setListarProdutos}
          />

          <Checkbox
            rotulo="Exibir 3ª linha para assinatura do consultor"
            checked={assinaturaConsultor}
            onChange={setAssinaturaConsultor}
          />
        </div>
      </FormSection>

      <FormSection titulo="Situação" icone="alerta">
        <Checkbox
          rotulo="Template ativo"
          destaque
          /* Era só a COR do texto (verde/vermelho) mais o rótulo mudando. */
          descricao={
            ativo
              ? 'O tipo é gerável: aparece na tela de geração de termo do consultor.'
              : 'O tipo está desabilitado para geração e não aparece na tela do consultor.'
          }
          checked={ativo}
          onChange={setAtivo}
        />
      </FormSection>

      <FormActions>
        <Botao variante="secundario" onClick={voltar} disabled={salvando || previewando}>
          Cancelar
        </Botao>
        <Botao
          variante="secundario"
          carregando={previewando}
          disabled={salvando}
          onClick={() => void preVisualizar()}
        >
          Pré-visualizar em PDF
        </Botao>
        <Botao carregando={salvando} disabled={previewando} onClick={() => void salvar()}>
          Salvar template
        </Botao>
      </FormActions>
    </div>
  );

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={6} altura="tw-h-12" />;

    if (estado === 'inexistente') {
      return (
        <EmptyState
          icone="sem-dados"
          titulo="Este tipo não está no catálogo"
          descricao={`O endereço aponta para o código ${codigo}, que não é um tipo de termo cadastrado.`}
          acao={
            <Botao variante="secundario" onClick={() => navigate(TITERMOS())}>
              Voltar para o catálogo
            </Botao>
          }
        />
      );
    }

    if (estado === 'erro') {
      /* 🔴 O sentinela da branch: falha de carga NUNCA vira formulário zerado —
         o TI poderia salvar o vazio por cima das cláusulas reais (§3.1). */
      return (
        <ErrorState
          mensagem="Não foi possível carregar o template."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    const situacao = template ? rotuloSituacaoTemplate(template) : null;

    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-3">
          {situacao && (
            <>
              <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                <Chip variante={situacao.variante}>{situacao.texto}</Chip>
              </div>
              {/* A explicação do estado, que era `title` na lista antiga. */}
              <p className="tw-m-0 tw-text-xs tw-text-texto-suave">{situacao.descricao}</p>
            </>
          )}

          <ResumoRegistro
            linhas={[
              { rotulo: 'Código do tipo', valor: codigo },
              {
                rotulo: 'Última edição',
                valor: template?.atualizadoEm ? formatarDataCurta(template.atualizadoEm) : '—',
              },
            ]}
          />
        </div>

        <Tabs
          idBase={ID_BASE}
          abas={ABAS}
          ativa={aba}
          aoTrocar={(valor) => setAba(valor as Aba)}
          rotuloAcessivel="Seções do template"
        />

        <div
          id={idDoPainel(ID_BASE)}
          role="tabpanel"
          aria-labelledby={idDaAba(ID_BASE, aba)}
          tabIndex={-1}
        >
          {aba === 'conteudo' ? (
            painelConteudo()
          ) : (
            <HistoricoEdicoes
              itens={historico.map((versao) => ({
                id: versao.id,
                quando: versao.criadoEm,
                resumo: (
                  <>
                    <strong>{versao.titulo}</strong> · {versao.ativo ? 'Ativo' : 'Inativo'}
                  </>
                ),
                detalhe: (
                  <pre className="tw-m-0 tw-max-h-40 tw-overflow-auto tw-whitespace-pre-wrap tw-rounded-lg tw-bg-superficie tw-p-2 tw-font-mono tw-text-xs tw-text-texto-corpo">
                    {versao.corpo}
                  </pre>
                ),
              }))}
              carregando={carregandoHistorico}
              erro={erroHistorico || undefined}
              aoTentarNovamente={() => void carregarHistorico()}
              vazio="Nenhuma edição registrada para este template."
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo={nome || `Tipo ${codigo}`}
          sobretitulo="Administração de Termos"
          aoVoltar={voltar}
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-5xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            {conteudo()}
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default TermoFichaPage;
